import { Outlet, NavLink, Navigate, useLocation } from "react-router-dom";
import {
  ShoppingCart,
  Utensils,
  Truck,
  Package,
  History as HistoryIcon,
  BarChart3,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  Receipt,
  Menu as MenuIcon,
  Minimize2,
  LogOut,
  User as UserIcon,
  Users,
  Phone,
  Keyboard as KeyboardIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth, usePermissions } from "@/lib/AuthContext";
import { useNavGuard } from "@/lib/NavGuardContext";
import { useKeyboard } from "@/lib/KeyboardContext";
import ManagerOverrideDialog, { ManagerOverrideBadge } from "@/components/ManagerOverrideDialog";
import CallerLookupDialog from "@/components/CallerLookupDialog";
import VirtualKeyboard from "@/components/VirtualKeyboard";
import PrintHealthBadge from "@/components/PrintHealthBadge";
import OperatorsOnlineBadge from "@/components/OperatorsOnlineBadge";
import FTextHealthBadge from "@/components/FTextHealthBadge";

export default function Layout() {
  const [health, setHealth] = useState(null);
  const [settings, setSettings] = useState(null);
  const [shift, setShift] = useState(null);
  const { user, logout, override, overridePrompt, dismissOverridePrompt,
          setShift: setAuthShift, setNeedsShiftOpen } = useAuth();
  const { can } = usePermissions();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [callerOpen, setCallerOpen] = useState(false);
  const { dirty, message } = useNavGuard();
  const { open: vkOpen, toggle: vkToggle, setOpen: vkSetOpen } = useKeyboard();
  const guardedClick = (e) => {
    if (!dirty) return;
    e.preventDefault();
    toast.error(message || "Ολοκλήρωσε την τρέχουσα παραγγελία πριν φύγεις");
  };
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar_collapsed") === "1";
    } catch {
      return false;
    }
  });
  const location = useLocation();

  const refresh = async () => {
    try {
      const [h, s, sh] = await Promise.all([
        http.get("/health"),
        http.get("/settings"),
        http.get("/shifts/current").catch(() => ({ data: { shift: null } })),
      ]);
      setHealth(h.data);
      setSettings(s.data);
      const currentShift = sh.data?.shift || null;
      setShift(currentShift);
      // Keep AuthContext in sync so PostLoginShiftPrompt can re-fire
      // if the operator closed their shift mid-session from /shifts.
      setAuthShift?.(currentShift);
      setNeedsShiftOpen?.(!currentShift);
    } catch {
      setHealth({ ok: false });
    }
  };

  useEffect(() => {
    const t0 = setTimeout(refresh, 0);
    const t = setInterval(refresh, 8000);
    return () => {
      clearTimeout(t0);
      clearInterval(t);
    };
  }, []);

  // NB: heartbeat + MessagesListener moved to GlobalUserSideEffects
  // (mounted from App.js) so they run for BOTH desktop Layout AND
  // MobileRestaurant (waiter) branches. See iter_60 audit.

  const toggleSidebar = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem("sidebar_collapsed", next ? "1" : "0");
      } catch {
        /* noop */
      }
      return next;
    });
  };

  /**
   * Feb 2026: «Ελαχιστοποίηση» — send the browser window to the Windows
   * taskbar without closing the app.
   *
   * Web JS can't minimize the browser directly, so we delegate to the
   * local Companion agent (v2+) on 127.0.0.1:9100 which calls
   * ``ShowWindow(GetForegroundWindow(), SW_MINIMIZE)``. When the agent
   * is unavailable (dev / non-kiosk browser) we fall back to
   * ``window.blur()`` and show a helpful toast so the operator knows
   * they can press Win+D as a workaround.
   */
  const handleMinimize = async () => {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 1500);
      const r = await fetch("http://127.0.0.1:9100/kiosk/minimize", {
        method: "POST",
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (r.ok) return; // silent success
      throw new Error(`HTTP ${r.status}`);
    } catch {
      // Graceful degradation: browsers can't natively minimize.
      try { window.blur(); } catch { /* ignore */ }
      toast.info("Ο τοπικός agent δεν απάντησε. Πάτησε Win+D για γρήγορο minimize.");
    }
  };

  const modules = {
    retail: settings?.modules_retail !== false,
    rest: settings?.modules_rest !== false,
    delivery: settings?.modules_delivery === true,
    customers: settings?.modules_delivery === true || settings?.modules_customers !== false,
  };

  const navItems = [
    { to: "/pos",        label: "Ταμείο",     icon: ShoppingCart, testid: "nav-pos",        needsModule: "retail",   perm: "module_retail" },
    { to: "/restaurant", label: "Εστιατόριο", icon: Utensils,     testid: "nav-restaurant", needsModule: "rest",     perm: "module_restaurant" },
    { to: "/delivery",   label: "Delivery",   icon: Truck,        testid: "nav-delivery",   needsModule: "delivery", perm: "module_delivery" },
    { to: "/catalog",    label: "Είδη",       icon: Package,      testid: "nav-catalog",    perm: "view_items" },
    { to: "/customers",  label: "Πελάτες",    icon: Users,        testid: "nav-customers",  needsModule: "customers" },
    { to: "/history",    label: "Ιστορικό",   icon: HistoryIcon,  testid: "nav-history",    perm: "reports_history" },
    { to: "/reports",    label: "Αναφορές",   icon: BarChart3,    testid: "nav-reports",    perm: "reports_cash_register" },
    { to: "/settings",   label: "Ρυθμίσεις",  icon: SettingsIcon, testid: "nav-settings",   perm: "view_settings" },
    { to: "/admin",      label: "Διαχείριση", icon: ShieldAlert,  testid: "nav-admin",      perm: "view_admin" },
  ];
  const visible = navItems.filter((n) => {
    if (n.needsModule && !modules[n.needsModule]) return false;
    if (n.perm && !can(n.perm)) return false;
    return true;
  });

  // If current route is no longer permitted, redirect to first allowed page.
  const currentItem = navItems.find((n) => n.to === location.pathname);
  const routeDeniedByModule = currentItem?.needsModule && !modules[currentItem.needsModule];
  const routeDeniedByPerm = currentItem?.perm && !can(currentItem.perm);
  if ((routeDeniedByModule || routeDeniedByPerm) && settings) {
    const first = visible[0]?.to || "/shifts";
    return <Navigate to={first} replace />;
  }

  return (
    <div className="h-screen w-screen flex bg-white overflow-hidden">
      <aside
        className={`shrink-0 h-full bg-slate-100 border-r-2 border-slate-300 flex flex-col transition-[width] duration-200 ease-out ${
          collapsed ? "w-14" : "w-44"
        }`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <button
          type="button"
          onClick={toggleSidebar}
          data-testid="sidebar-toggle"
          title={collapsed ? "Εμφάνιση μενού" : "Απόκρυψη μενού"}
          className="w-full border-b-2 border-slate-300 bg-slate-50 hover:bg-slate-200 active:scale-[0.98] transition-all text-left p-0 overflow-hidden"
        >
          <div className="flex items-start justify-start w-full">
            <img
              src={settings?.business_logo || "/ponte-pos-logo-square.jpg"}
              alt="Ponte Pos"
              className="w-full h-auto object-cover transition-all duration-200"
              style={{ display: "block" }}
            />
          </div>
        </button>

        <nav className={`flex-1 ${collapsed ? "px-1.5" : "px-2.5"} py-3 space-y-2 overflow-y-auto scroll-thin`}>
          {visible.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              onClick={guardedClick}
              title={collapsed ? it.label : undefined}
              className={({ isActive }) =>
                `block rounded-lg transition-all border-2 select-none ${
                  isActive
                    ? "bg-blue-600 text-white border-blue-700 shadow-md translate-y-0"
                    : "bg-white text-slate-800 border-slate-300 shadow-sm hover:bg-slate-50 hover:border-slate-400 active:translate-y-0.5 active:shadow-none"
                }`
              }
            >
              <div className={`flex items-center ${collapsed ? "justify-center px-2 py-3" : "gap-2.5 px-3 py-3"}`}>
                <it.icon size={20} className="shrink-0" />
                {!collapsed && (
                  <span className="text-base font-bold font-heading leading-none">{it.label}</span>
                )}
              </div>
            </NavLink>
          ))}
          {/* Feb 2026: «Ελαχιστοποίηση» button — sends the browser to
              the Windows taskbar without closing the app. Requires the
              local Companion agent (v2+) on 127.0.0.1:9100 which
              exposes /window/minimize. In dev / browsers without the
              agent it falls back to window.blur() and a helpful toast. */}
          {user && (
            <button
              type="button"
              onClick={handleMinimize}
              data-testid="nav-minimize"
              title={collapsed ? "Ελαχιστοποίηση" : undefined}
              className="block w-full rounded-lg transition-all border-2 select-none bg-white text-slate-800 border-slate-300 shadow-sm hover:bg-slate-50 hover:border-slate-400 active:translate-y-0.5 active:shadow-none"
            >
              <div className={`flex items-center ${collapsed ? "justify-center px-2 py-3" : "gap-2 px-2 py-3"}`}>
                <Minimize2 size={18} className="shrink-0" />
                {!collapsed && (
                  <span className="text-xs font-bold font-heading leading-tight truncate">
                    Ελαχιστοποίηση
                  </span>
                )}
              </div>
            </button>
          )}
        </nav>

        <div className={`${collapsed ? "px-1.5" : "px-3"} py-3 border-t-2 border-slate-300 bg-slate-50 text-[11px] space-y-2`}>
          {/* Manager-override badge + request button — only when no
              override is already active the operator sees the «Έγκριση
              από Manager» trigger; the badge replaces it during the
              elevated window. */}
          {!collapsed && user && (
            override ? (
              <ManagerOverrideBadge />
            ) : (
              user.role !== "manager" && user.role !== "super_manager" && (
                <button
                  type="button"
                  onClick={() => setOverrideOpen(true)}
                  data-testid="manager-override-open"
                  className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-800 text-[11px] font-bold transition-colors"
                >
                  <ShieldCheck size={12} />
                  Έγκριση από Manager
                </button>
              )
            )
          )}
          {/* NEW (Feb 2026): show who else is online across the tenant.
              Sits above the printer health badge so managers see the
              team roster first, then the printer queue status. */}
          {user && <OperatorsOnlineBadge collapsed={collapsed} />}
          {/* Live print pipeline health badge — visible on every screen so
              the owner notices stuck queue jobs / mis-routed products
              before waiters start complaining. Hidden for the waiter
              role (they can't act on it). */}
          {user && (can("admin") || user.role === "super_manager" || user.role === "manager") && (
            <PrintHealthBadge collapsed={collapsed} />
          )}
          {/* User + shift block */}
          {user && (
            <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2"}`}>
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white grid place-items-center shrink-0 font-bold">
                <UserIcon size={14} />
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-800 truncate" title={user.name}>{user.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{user.role}</div>
                </div>
              )}
              {!collapsed && (
                <button
                  type="button"
                  onClick={(e) => {
                    if (dirty) {
                      e.preventDefault();
                      toast.error(message || "Ολοκλήρωσε την τρέχουσα παραγγελία πριν φύγεις");
                      return;
                    }
                    logout();
                  }}
                  title="Αποσύνδεση"
                  data-testid="logout-btn"
                  className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <LogOut size={14} />
                </button>
              )}
            </div>
          )}
          {!collapsed && user && !shift && (
            <NavLink
              to="/shifts"
              data-testid="shifts-link"
              onClick={guardedClick}
              className={({ isActive }) =>
                `block rounded-md px-2 py-1.5 text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 ${
                  isActive ? "ring-1 ring-slate-400" : "hover:brightness-95"
                }`
              }
            >
              <span className="flex items-center gap-1.5">
                <Users size={12} />
                Κλειστή Βάρδια
              </span>
            </NavLink>
          )}
          {/* Fiscal cash register connectivity (renamed Feb 2026):
              vivid green when the local driver responds, vivid red on
              disconnect. The label was previously "Driver Online" —
              operators wanted "Κατάσταση Ταμειακής" instead. */}
          <div
            className={`flex items-center rounded-md ${
              collapsed
                ? "justify-center p-1.5"
                : "gap-2 px-2 py-1.5 border"
            } ${
              health?.ok
                ? "bg-emerald-100 border-emerald-400 text-emerald-800"
                : "bg-red-100 border-red-400 text-red-800"
            }`}
            data-testid="fiscal-status-block"
            title={
              health?.ok
                ? health.simulator_mode
                  ? "Ταμειακή σε λειτουργία Simulator"
                  : "Ταμειακή Online"
                : "Ταμειακή Offline — δεν εντοπίζεται ο driver"
            }
          >
            <span
              className={`rounded-full shrink-0 ${
                collapsed ? "w-3 h-3" : "w-3 h-3"
              } ${
                health?.ok
                  ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.9)] animate-pulse"
                  : "bg-red-600 shadow-[0_0_8px_rgba(220,38,38,0.9)]"
              }`}
              data-testid="fiscal-status-dot"
            />
            {!collapsed && (
              <div className="flex-1 min-w-0 leading-tight">
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">
                  Κατάσταση Ταμειακής
                </div>
                <div className="text-xs font-extrabold uppercase truncate">
                  {health?.ok
                    ? health.simulator_mode
                      ? "Simulator"
                      : "Online"
                    : "Offline"}
                </div>
              </div>
            )}
          </div>
          {/* FTEXT Queue health — Feb 2026 self-diagnosis + auto-repair.
              Δείχνει live αν κολλάνε αποδείξεις στην IRIS (error 108) και
              επιτρέπει «Επιδιόρθωση Τώρα» με ένα κλικ. */}
          {!collapsed && (
            <div className="mt-1 flex items-center gap-1.5" data-testid="ftext-health-wrap">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                FTEXT
              </span>
              <FTextHealthBadge />
            </div>
          )}
          {/* Virtual-keyboard toggle — global, persists across pages.
              Useful on tablet-only deployments where there's no
              physical keyboard. */}
          <button
            type="button"
            onClick={vkToggle}
            data-testid="sidebar-kb-toggle"
            title={vkOpen ? "Απόκρυψη πληκτρολογίου" : "Εμφάνιση πληκτρολογίου"}
            className={`mt-1 rounded-md transition-all flex items-center ${
              collapsed ? "p-2 justify-center" : "px-3 py-2 gap-2"
            } ${
              vkOpen
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300"
            }`}
          >
            <KeyboardIcon size={collapsed ? 18 : 16} />
            {!collapsed && (
              <span className="text-xs font-bold uppercase tracking-wide">
                {vkOpen ? "Πληκτρ. ON" : "Πληκτρολόγιο"}
              </span>
            )}
          </button>
        </div>
      </aside>

      <main className="flex-1 h-full overflow-hidden bg-white">
        <Outlet />
      </main>
      {/* Sidebar-triggered override (when the operator clicks the
          «Έγκριση από Manager» button). */}
      <ManagerOverrideDialog open={overrideOpen} onOpenChange={setOverrideOpen} />
      {/* Inline-triggered override (when a guarded button toast's
          «Ζητήστε έγκριση Manager» action is clicked). On grant, the
          pending action stored in overridePrompt.onGranted fires. */}
      <ManagerOverrideDialog
        open={!!overridePrompt}
        onOpenChange={(o) => { if (!o) dismissOverridePrompt(); }}
        reason={overridePrompt?.reason}
        onGranted={() => {
          const cb = overridePrompt?.onGranted;
          dismissOverridePrompt();
          if (typeof cb === "function") cb();
        }}
      />
      <CallerLookupDialog open={callerOpen} onOpenChange={setCallerOpen} />
      {/* Global on-screen keyboard — single instance, toggled from
          sidebar. Mounted at the layout root so it survives route
          transitions and floats above all page content (z-[60]). */}
      <VirtualKeyboard open={vkOpen} onOpenChange={vkSetOpen} />
    </div>
  );
}
